import { animateChild, AnimationEvent, query, transition, trigger } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ComponentBase } from '@base/base.component.base';
import { Observable } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { ICloseMessage, IMessage } from './model/message.model';
import { MessageService } from './service/message.service';

@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  animations: [trigger('toastAnimation', [transition(':enter, :leave', [query('@*', animateChild())])])]
})

// note - strongly based off of primeFaces - https://primefaces.org/primeng/showcase/#/toast
export class ToastComponent extends ComponentBase implements OnInit {
  @Input() autoZIndex = true;
  @Input() baseZIndex = 0;
  @Input() styleClass = '';
  @Input() position = 'top-center';
  @Input() showTransitionOptions = '300ms ease-out';
  @Input() hideTransitionOptions = '250ms ease-in';

  @Output() closeToast: EventEmitter<ICloseMessage> = new EventEmitter<ICloseMessage>();
  @ViewChild('container', { static: false }) containerViewChild!: ElementRef<HTMLDivElement>;

  messages: IMessage[] = [];

  constructor(public messageService: MessageService) {
    super();
  }

  ngOnInit(): void {
    this.listenForMessages().subscribe();
    this.listenForClearEvent().subscribe();
  }

  /**
   * On Init subscribes to messages service. On destroy unsubscribes.
   * @returns Observable of Message or array of Messages.
   */
  listenForMessages(): Observable<IMessage | IMessage[]> {
    return this.messageService.getMessageSource().pipe(
      filter((m) => !!m),
      tap((messages: IMessage | IMessage[]) => {
        if (messages instanceof Array) {
          this.messages = [...this.messages, ...messages];
        } else {
          this.messages = [...this.messages, ...[messages]];
        }
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Listen to the messageService for clearing of messages.
   * @returns Observable string.
   */
  listenForClearEvent(): Observable<string> {
    return this.messageService.getClearObserver().pipe(
      tap(() => {
        this.messages = [];
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  /**
   * Action to take on closing of message - through timeout or manual close.
   * @param event index and message.
   */
  onMessageClose(event: ICloseMessage): void {
    this.messages.splice(event.index, 1);

    this.closeToast.emit({
      index: event.index,
      message: event.message
    });
  }

  /**
   * Triggers on Animation start from template.
   * @param event Animation event.
   */
  onAnimationStart(event: AnimationEvent): void {
    if (event.fromState === 'void' && this.autoZIndex) {
      this.containerViewChild.nativeElement.setAttribute('style', `z-index: ${String(this.baseZIndex + 1001)}`);
    }
  }
}
